import { useState } from 'react';
import { useValidationStats } from '@/hooks/use-validation-stats';
import { Skeleton } from '@/components/ui/skeleton';

interface ValidationFilterPanelProps {
  onSearchChange?: (search: string) => void;
  onStatusFilter?: (status: 'pending' | 'review' | 'validated' | null) => void;
  onSortChange?: (sort: 'priority' | 'date' | 'amount') => void;
}

export const ValidationFilterPanel = ({ 
  onSearchChange, 
  onStatusFilter, 
  onSortChange 
}: ValidationFilterPanelProps) => {
  const [search, setSearch] = useState('');
  const [activeStatus, setActiveStatus] = useState<'pending' | 'review' | 'validated' | null>(null);
  const [sortBy, setSortBy] = useState<'priority' | 'date' | 'amount'>('priority');
  const [showFilters, setShowFilters] = useState(false);
  
  // Get real validation statistics from database
  const { data: validationStats, isLoading } = useValidationStats();

  const handleSearchChange = (value: string) => {
    setSearch(value);
    onSearchChange?.(value);
  };

  const handleStatusClick = (status: 'pending' | 'review' | 'validated') => {
    const newValue = activeStatus === status ? null : status;
    setActiveStatus(newValue);
    onStatusFilter?.(newValue);
  };

  const handleSortClick = () => {
    const sortOptions: Array<'priority' | 'date' | 'amount'> = ['priority', 'date', 'amount'];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextSort = sortOptions[(currentIndex + 1) % sortOptions.length];
    setSortBy(nextSort);
    onSortChange?.(nextSort);
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'priority': return 'Priority Level';
      case 'date': return 'Date';
      case 'amount': return 'Amount';
    }
  };

  return (
    <section className="bg-white p-4 rounded-2xl border border-brand-border mb-6 space-y-4">
      {/* Main Filter Row */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <input 
              type="text" 
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search transactions, accounts..." 
              className="w-full h-9 pl-4 pr-10 bg-brand-secondary rounded-lg text-xs text-brand-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-accent"
            />
            <i className="fa-solid fa-magnifying-glass text-brand-text-secondary absolute right-4 top-1/2 -translate-y-1/2"></i>
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="h-9 px-3 py-2 bg-brand-secondary rounded-lg text-xs text-brand-primary font-medium flex items-center gap-2 hover:bg-brand-accent/10 transition-colors"
          >
            <i className="fa-solid fa-sliders"></i> Advanced Filters
          </button>
          <div className="h-6 w-px bg-brand-border"></div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-brand-text-secondary">Status:</span>
            <button 
              onClick={() => handleStatusClick('pending')}
              className={`px-2 py-1 text-xs rounded-full transition-all ${
                activeStatus === 'pending' 
                  ? 'bg-red-600 text-white ring-2 ring-red-300' 
                  : 'bg-validation-critical-bg text-validation-critical hover:bg-red-200'
              }`}
            >
              Pending
            </button>
            <button 
              onClick={() => handleStatusClick('review')}
              className={`px-2 py-1 text-xs rounded-full transition-all ${
                activeStatus === 'review' 
                  ? 'bg-orange-600 text-white ring-2 ring-orange-300' 
                  : 'bg-validation-high-bg text-validation-high hover:bg-orange-200'
              }`}
            >
              In Review
            </button>
            <button 
              onClick={() => handleStatusClick('validated')}
              className={`px-2 py-1 text-xs rounded-full transition-all ${
                activeStatus === 'validated' 
                  ? 'bg-green-600 text-white ring-2 ring-green-300' 
                  : 'bg-validation-low-bg text-validation-low hover:bg-green-200'
              }`}
            >
              Validated
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-brand-text-secondary">Sort by:</span>
          <button 
            onClick={handleSortClick}
            className="h-9 px-3 py-2 bg-brand-secondary rounded-lg text-xs text-brand-primary font-medium flex items-center gap-2 hover:bg-brand-accent/10 transition-colors"
          >
            {getSortLabel()} <i className="fa-solid fa-arrow-down-long"></i>
          </button>
        </div>
      </div>
      
      {/* Priority Stats Row - Real-time data from database */}
      <div className="flex items-center gap-6 pt-2 border-t border-brand-border">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-validation-critical rounded-full"></div>
              <span className="text-xs font-medium text-brand-text-primary">
                {validationStats?.critical || 0} Critical
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-validation-high rounded-full"></div>
              <span className="text-xs font-medium text-brand-text-primary">
                {validationStats?.high || 0} High Priority
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-validation-medium rounded-full"></div>
              <span className="text-xs font-medium text-brand-text-primary">
                {validationStats?.medium || 0} Medium
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-validation-low rounded-full"></div>
              <span className="text-xs font-medium text-brand-text-primary">
                {validationStats?.low || 0} Low
              </span>
            </div>
            <div className="h-4 w-px bg-brand-border"></div>
            <span className="text-xs font-medium text-brand-text-secondary">
              Showing {validationStats?.total || 0} items requiring validation
            </span>
          </>
        )}
      </div>
    </section>
  );
};